import { round_2decimals } from '~/utils/lib/core/float'
import { filter, not, pipe, replace } from '~/utils/lib/core/prelude'
/**
 * Component status based on UI/UX overall expression
 * @enum {typeof STATUS[keyof typeof STATUS]}
 *
 * @property {string} enabled"
 *      default UI/UX behavior
 *
 * @property {string} disabled
 *      main action of the component has been deactivated, and
 *      a UI feedback is showefamsefamsed to the user (eg. a disabled button is now
 *      unclikable, fade color, and disabled CTA feature)
 *
 * @property {string} deffered
 *      main action of the component has been deactivated, but
 *      contrary to a disabled state, the altered UX/UI bears characteristics
 *      of a short-lived state (eg. a button turns to `Deffered`, is now
 *      unclickable but presents a less inoperative style, for example a
 *      spinner is now attached in place of the CTA text)
 *
 * @property {string} muted
 *      on surface the component seems functional, but main action
 *      of the component has been deactivated, yet no UI nor UX feedback is
 *      particularly showed to the user accordingly
 *
 * @property {string} idled
 *      balance between a `Disabled` and `Muted` state, as if the
 *      component has its main feature deactivated, but told with a less
 *      strong UI/UX (eg. a input in a "read-only" mode: UI can be alter to
 *      underline the lack of its main writing feature, but without telling
 *      to the user that the input is per-se inoperative)
 *
 */
export const STATUS = /** @type {const} */ ({
  ENABLED   : 'enabled',
  DISABLED  : 'disabled',
  DEFFERED  : 'deffered',
  MUTED     : 'muted',
  IDLED     : 'idled',
})

/**
 * Component size based on "bootstrap-vue-next" simplified sizing
 * (eg. "btn")
 *
 * @XXX "bootstrap-vue-next" Medium (`md`) is our explicit default.
 *    ↳ description: dropped from version ≥0.45 (`Size` is now only `sm` | `lg`)
 *    ↳ workaround: map at the BVN boundary with {@link toBvnSize}
 * @link https://github.com/bootstrap-vue-next/bootstrap-vue-next/pull/3178
 *
 * @enum {typeof SIZE[keyof typeof SIZE]} SIZE
 */
export const SIZE = /** @type {const} */ ({
  SMALL   : 'sm',
  MEDIUM  : 'md',
  LARGE   : 'lg'
})

/**
 * Map app {@link SIZE} → bootstrap-vue-next `Size`.
 * `md` / omit → `undefined` (Bootstrap default: no size class).
 *
 * @param {typeof SIZE[keyof typeof SIZE]|null|undefined} [size]
 * @returns {import('bootstrap-vue-next').Size|undefined}
 */
export function toBvnSize(size) {
  if (size === SIZE.SMALL || size === SIZE.LARGE)
    return size

  return undefined
}

/**
 * Project colour variant
 *    - contains "bootstrap" theme colours [1]
 *    - additional custom variant (eg. `"tertiary"` → cf "_variables".scss")
 *
 * @enum { import('bootstrap-vue-next').ColorVariant
 *       | "tertiary"
 *       } VARIANT
 */
export const VARIANT = /** @type {const} */ ({
  PRIMARY   : 'primary',
  SECONDARY : 'secondary',
  TERTIARY  : 'tertiary',
  DANGER    : 'danger',
  SUCCESS   : 'success',
  WARNING   : 'warning',
  INFO      : 'info',
  LIGHT     : 'light',
  DARK      : 'dark',
})
/**
 * Project token colour variant
 *    - cf. "variables/abstract"
 *
 * @enum { "red"
 *       | "orange"
 *       | "yellow"
 *       | "green"
 *       | "blue"
 *       | "purple"
 *       | "cyan"
 *       | "pink"
 *       | "magenta"
 *       } TOKEN_VARIANT
 */
export const TOKEN_VARIANT = /** @type {const} */ ({
  RED : 'red',
  ORANGE : 'orange',
  YELLOW : 'yellow',
  GREEN : 'green',
  BLUE : 'blue',
  PURPLE : 'purple',
  CYAN : 'cyan',
  PINK : 'pink',
  MAGENTA : 'magenta',
})


/**
 * Constant values for elevation purposes (mostly dealing internally by
 * each component via shadow or overlay alternatives)
 *
 * @enum {typeof ELEVATION[keyof typeof ELEVATION]}
 * @readonly
 *
 * @property {string} LEVEL_0
 * @property {string} LEVEL_1
 * @property {string} LEVEL_2
 */
export const ELEVATION = /** @type {const} */ ({
  LEVEL_0: 'level-0',
  LEVEL_1: 'level-1',
  LEVEL_2: 'level-2',
})

/**
 * "bootstrap-vue-next" spinner type reference
 *
 * @enum { import('bootstrap-vue-next').SpinnerType
 *       } SPINNER_TYPE
 *
 * @readonly
 * @desc "bootstrap-vue-next" <b-spinner> type
 * @link https://bootstrap-vue.org/docs/components/spinner#spinner-types
 *
 * @property {string} BORDER circular string
 * @property {string} GROW growing circles
 */
export const SPINNER_TYPE = /** @type {const} */ ({
  BORDER: 'border',
  GROW  : 'grow',
})

/**
 * Material Design Icon themes
 *
 * @enum { 'filled'
 *       | 'outlined'
 *       | 'two-tone'
 *       | 'round'
 *       | 'sharp'
 *       } ICON_THEME
 */
export const ICON_THEME = /** @type {const} */ ({
  FILLED    : 'filled', // mostly the default one
  OUTLINED  : 'outlined',
  TWO_TONED : 'two-tone',
  ROUNDED   : 'round',
  SHARP     : 'sharp'
})

/**
 * Element positions (eg. for calendar element in <ui-form-date-single>)
 *
 * @typedef { "auto"
 *          | "left"
 *          | "right"
 *          } HORIZONTAL_POSITION
 *
 * @typedef { "auto"
 *          | "top"
 *          | "bottom"
 *          } VERTICAL_POSITION
 */

/**
 * Calendar mode (eg. in <ui-form-date-single>)
 *
 * @typedef { "calendar-only"
 *          | "calendar-with-input"
 *          } CALENDAR_MODE
 */

/**
 * Underline behavior (eg. for anchor Element)
 *
 * Following every major design system, breaking down an architectural pattern
 * for Element usually bearing user stylesheet agents' default style
 *
 * @typedef { "none"
 *          | "hover"
 *          | "always"
 *          } UNDERLINE
 */

/**
 * Focus ring behavior (eg. for interactive anchor / button-link elements)
 *
 * Mirrors the {@link UNDERLINE} opt-in pattern: off by default, on focus/click
 * (as bootstrap-vue-next <b-button>), or permanently on.
 *
 * @typedef { "none"
 *          | "active"
 *          | "always"
 *          } FOCUS_RING
 */

/**
 * Focus ring colour (eg. for {@link FOCUS_RING} on interactive elements)
 *
 * When undefined, the ring uses the global `$focus-ring-color` token.
 * When set, the ring uses the matching semantic palette colour.
 *
 * @typedef { VARIANT[keyof VARIANT] | undefined } FOCUS_RING_VARIANT
 */

/// @XXX "bootstrap-vue-next" using TypeScript typing without exporting them
/**
 * @typedef { string
 *          | number
 *          } Numberish
 */

/**
 * @function parseWithHTMLNewLine
 * @desc
 *    Parse new line characters with HTML new line tag
 * @example
 *    <div v-html="parseWithHTMLNewLine( 'hello \n how are you' )" />
 * @link https://stackoverflow.com/a/54980055/6003907
 */
export const parseWithHTMLNewLine = replace(/(?:\r\n|\r|\n)/g, '<br>')
/**
 * @function pixelValueToNumber
 * @desc From CSS pixel value to JavaScript number
 * @example
 *    pixelValueToNumber('14.5px') // 14.5
 */
export const pixelValueToNumber = pipe(
  replace(/(px)$/, ''),
  round_2decimals
)
/**
 * @function isElementFocused
 * @desc Checking whether the element is currently the `activeElement`
 *       (works best for form-type Element)
 * @param {HTMLElement} element
 * @return {boolean}
 */
export function isElementFocused(element) {
  return window.document.activeElement === element
}
/** @readonly */
const focussableQuerySelector = 'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])'
/**
 * @function focusNextElement
 * @desc Will set focus into the next DOM tabindex Element
 * @link https://stackoverflow.com/a/35173443/6003907
 */
export function focusNextElement() {
  const hasCurrentActiveElement
     = document.activeElement
    // @ts-ignore: IDK
    && document.activeElement.form

  if (not(hasCurrentActiveElement))
    return

  // @ts-ignore: IDK
  const elementList = document.activeElement.form.querySelectorAll(
    focussableQuerySelector
  )
  //check for visibility while always include the current activeElement
  const elementList_ = filter(
    element => (
      element.offsetWidth > 0 ||
      element.offsetHeight > 0 ||
      element === document.activeElement
    ),
    elementList
  )

  const index = elementList_.indexOf(document.activeElement)

  if (index === -1)
    return

  const nextElement
     = elementList_[index + 1]
    || elementList_[0] // (re-lapping tabindex)

  nextElement.focus()
}
/**
 * @function focusPreviousElement
 * @desc Will set focus into the previous DOM tabindex Element
 * @link https://stackoverflow.com/a/35173443/6003907
 */
export function focusPreviousElement() {
  const hasCurrentActiveElement
     = document.activeElement
    // @ts-ignore: IDK
    && document.activeElement.form

  if (not(hasCurrentActiveElement))
    return

  // @ts-ignore: IDK
  const elementList = document.activeElement.form.querySelectorAll(
    focussableQuerySelector
  )
  //check for visibility while always include the current activeElement
  const elementList_ = filter(
    element => (
      element.offsetWidth > 0 ||
      element.offsetHeight > 0 ||
      element === document.activeElement
    ),
    elementList
  )

  const index = elementList_.indexOf(document.activeElement)

  if (index === -1)
    return

  const previousElement
     = elementList_[index - 1]
    || elementList_[ elementList_.length - 1 ] // (re-lapping tabindex)

  previousElement.focus()
}
